import React, { useState } from 'react';
import styled from 'styled-components';
import { createGlobalStyle } from 'styled-components';
import { GlobalStyleProps } from '../types/styles';
import { StyledProps } from 'types/styled';
import Sidebar from '../components/Sidebar';
import Header from '../components/AppBar';
import Settings from '../components/Settings';

import { Box, CssBaseline, useMediaQuery, useTheme } from '@mui/material';

const drawerWidth = 260;

const GlobalStyle = createGlobalStyle<GlobalStyleProps>`
  .MuiCardHeader-action .MuiIconButton-root {
    padding: 4px;
    width: 28px;
    height: 28px;
  }
`;

const Root = styled.div`
  display: flex;
  min-height: 100vh;
  background: transparent;
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up('md')} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  overflow-x: hidden;
  background: transparent;
`;

const MainContent = styled.div<StyledProps>`
  flex: 1;
  /* Bottom padding reserves clearance for the fixed "+" Fab (56px + 30px offset)
     so it never covers page footers such as table pagination controls. */
  padding: ${(props) => props.theme.spacing(12, 12, 24, 12)};
  padding-top: calc(64px + ${(props) => props.theme.spacing(6)});
  background: transparent;

  /* Smooth page transitions */
  & > * {
    animation: fadeInUp 0.4s ease-out;
  }

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  ${(props) => props.theme.breakpoints.down('lg')} {
    padding: ${(props) => props.theme.spacing(5, 5, 24, 5)};
    padding-top: calc(64px + ${(props) => props.theme.spacing(3)});
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

type DashboardPropsType = {
  // Note: routes prop removed - Sidebar uses sidebarRoutes from useAppContext() instead
  children?: React.ReactNode;
};

const Dashboard: React.FC<DashboardPropsType> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle theme={theme as any} />
      <Drawer>
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
          />
        </Box>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Sidebar PaperProps={{ style: { width: drawerWidth } }} />
        </Box>
      </Drawer>
      <AppContent>
        <Header onDrawerToggle={handleDrawerToggle} />
        <MainContent data-page-container>{children}</MainContent>
      </AppContent>
      <Settings />
    </Root>
  );
};

export default Dashboard;
